/**
 * The lookups behind the pricing pickers and name cells (#3034): categories, parts, companies and persons.
 *
 * The shared `useCategories` / `useInventoryParts` / `useCompanies` / `usePersons` hooks key their caches by filter only (no tenant, no
 * user, no session family) and are not fenced, so a name cached for another owner could be served to this one, and a late answer of
 * another session could land in the current cache. These are the screens' own queries of the SAME endpoints: keyed by the owner,
 * enabled only while the permission they need is held NOW, and fenced like every other pricing request. Nothing global is touched.
 */

import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import {
  partPricingKeys,
  runOwned,
  usePricingScope,
} from '@/hooks/usePartPricing';
import type { CategoryItem, PartDetail, PartFilters, PartListItem } from '@/hooks/useInventory';
import type { Company, CompanyFilters } from '@/hooks/useCompanies';
import type { Person, PersonFilters } from '@/hooks/usePersons';
import { PRICING_CUSTOMERS_PERMISSION, PRICING_READ_PERMISSION, usePricingAccess } from './pricingSession';

interface CursorPage<T> {
  data: T[];
  pagination: { nextCursor: string | null; prevCursor: string | null; hasMore: boolean };
}

const READ: readonly string[] = [PRICING_READ_PERMISSION];
const CUSTOMERS: readonly string[] = [PRICING_CUSTOMERS_PERMISSION];

/** Every category (the endpoint returns the whole tree). */
export function useScopedCategories() {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useQuery({
    queryKey: scope ? [...partPricingKeys.reference(scope), 'categories'] : ['inventory', 'part-pricing-ref', 'categories', 'signed-out'],
    queryFn: () => runOwned(access, READ, () => api.get<{ data: CategoryItem[] }>('/inventory/categories')),
    enabled: scope !== null && access.canRead,
    staleTime: 5 * 60 * 1000,
  });
}

export function useScopedParts(filters: PartFilters, options: { enabled?: boolean } = {}) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useInfiniteQuery({
    queryKey: scope ? [...partPricingKeys.reference(scope), 'parts', filters] : ['inventory', 'part-pricing-ref', 'parts', 'signed-out'],
    queryFn: ({ pageParam }) =>
      runOwned(access, READ, () => api.get<CursorPage<PartListItem>>('/inventory/parts', { params: { ...filters, cursor: pageParam } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.pagination.hasMore ? (lastPage.pagination.nextCursor ?? undefined) : undefined),
    staleTime: 30 * 1000,
    enabled: scope !== null && access.canRead && (options.enabled ?? true),
  });
}

export function useScopedPart(id: string | undefined) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useQuery({
    queryKey: scope ? [...partPricingKeys.reference(scope), 'part', id ?? ''] : ['inventory', 'part-pricing-ref', 'part', 'signed-out'],
    queryFn: () => runOwned(access, READ, () => api.get<PartDetail>(`/inventory/parts/${id}`)),
    enabled: scope !== null && access.canRead && !!id,
    staleTime: 60 * 1000,
  });
}

export function useScopedCompanies(filters: CompanyFilters, options: { enabled?: boolean } = {}) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useInfiniteQuery({
    queryKey: scope ? [...partPricingKeys.reference(scope), 'companies', filters] : ['inventory', 'part-pricing-ref', 'companies', 'signed-out'],
    queryFn: ({ pageParam }) =>
      runOwned(access, CUSTOMERS, () => api.get<CursorPage<Company>>('/companies', { params: { ...filters, cursor: pageParam } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.pagination.hasMore ? (lastPage.pagination.nextCursor ?? undefined) : undefined),
    staleTime: 30 * 1000,
    enabled: scope !== null && access.canReadCustomers && (options.enabled ?? true),
  });
}

export function useScopedPersons(filters: PersonFilters, options: { enabled?: boolean } = {}) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useInfiniteQuery({
    queryKey: scope ? [...partPricingKeys.reference(scope), 'persons', filters] : ['inventory', 'part-pricing-ref', 'persons', 'signed-out'],
    queryFn: ({ pageParam }) =>
      runOwned(access, CUSTOMERS, () => api.get<CursorPage<Person>>('/persons', { params: { ...filters, cursor: pageParam } })),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.pagination.hasMore ? (lastPage.pagination.nextCursor ?? undefined) : undefined),
    staleTime: 30 * 1000,
    enabled: scope !== null && access.canReadCustomers && (options.enabled ?? true),
  });
}

export function useScopedCompany(id: string | undefined) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useQuery({
    queryKey: scope ? [...partPricingKeys.reference(scope), 'company', id ?? ''] : ['inventory', 'part-pricing-ref', 'company', 'signed-out'],
    queryFn: () => runOwned(access, CUSTOMERS, () => api.get<Company>(`/companies/${id}`)),
    enabled: scope !== null && access.canReadCustomers && !!id,
    staleTime: 60 * 1000,
  });
}

export function useScopedPerson(id: string | undefined) {
  const access = usePricingAccess();
  const scope = usePricingScope();
  return useQuery({
    queryKey: scope ? [...partPricingKeys.reference(scope), 'person', id ?? ''] : ['inventory', 'part-pricing-ref', 'person', 'signed-out'],
    queryFn: () => runOwned(access, CUSTOMERS, () => api.get<Person>(`/persons/${id}`)),
    enabled: scope !== null && access.canReadCustomers && !!id,
    staleTime: 60 * 1000,
  });
}
